// Admin Configuration System
// Multi-level admin system with location-based permissions

export const SUPER_ADMIN_EMAILS = [
  'jogijacob89@gmail.com', // Primary Super Admin
];

export const ADMIN_PERMISSIONS = {
  // Donor Management
  VIEW_ALL_DONORS: 'view_all_donors',
  VIEW_LOCATION_DONORS: 'view_location_donors',
  EDIT_DONOR_PROFILES: 'edit_donor_profiles',
  DELETE_DONORS: 'delete_donors',
  EXPORT_DONOR_DATA: 'export_donor_data',
  
  // Request Management
  VIEW_ALL_REQUESTS: 'view_all_requests',
  VIEW_LOCATION_REQUESTS: 'view_location_requests',
  EDIT_REQUESTS: 'edit_requests',
  DELETE_REQUESTS: 'delete_requests',
  FORCE_FULFILL_REQUESTS: 'force_fulfill_requests',
  
  // System Management
  MANAGE_HOSPITALS: 'manage_hospitals',
  MANAGE_PARISHES: 'manage_parishes',
  VIEW_ANALYTICS: 'view_analytics',
  VIEW_LOCATION_ANALYTICS: 'view_location_analytics',
  SYSTEM_SETTINGS: 'system_settings',
  
  // Admin Management
  MANAGE_ADMINS: 'manage_admins',
  PROMOTE_USERS: 'promote_users',
  BAN_USERS: 'ban_users',
  VIEW_USER_ACTIVITY: 'view_user_activity',
};

export const ADMIN_ROLES = {
  SUPER_ADMIN: {
    name: 'Super Admin',
    permissions: Object.values(ADMIN_PERMISSIONS),
    description: 'Full system access and control. Can delete any admin including other admins.',
    locationRestricted: false,
    canDeleteSuperAdmin: true,
    canCreateAdmins: true
  },
  ADMIN: {
    name: 'Admin',
    permissions: Object.values(ADMIN_PERMISSIONS),
    description: 'Full permissions but cannot delete Super Admin. Can create District Admins.',
    locationRestricted: false,
    canDeleteSuperAdmin: false,
    canCreateAdmins: true
  },
  DISTRICT_ADMIN: {
    name: 'District Admin',
    permissions: [
      // Donor Management (district only)
      ADMIN_PERMISSIONS.VIEW_LOCATION_DONORS,
      ADMIN_PERMISSIONS.EDIT_DONOR_PROFILES,
      ADMIN_PERMISSIONS.DELETE_DONORS,
      ADMIN_PERMISSIONS.EXPORT_DONOR_DATA,
      // Request Management (district only)
      ADMIN_PERMISSIONS.VIEW_LOCATION_REQUESTS,
      ADMIN_PERMISSIONS.EDIT_REQUESTS,
      ADMIN_PERMISSIONS.FORCE_FULFILL_REQUESTS,
      // Analytics (district only)
      ADMIN_PERMISSIONS.VIEW_LOCATION_ANALYTICS,
    ],
    description: 'Can add, remove, and message donors only in their assigned district.',
    locationRestricted: true,
    canDeleteSuperAdmin: false,
    canCreateAdmins: false
  }
};

// Admin user schema for Firestore
export const createAdminSchema = (data) => ({
  id: data.id || null,
  email: data.email,
  name: data.name,
  role: data.role, // SUPER_ADMIN, REGIONAL_ADMIN, etc.
  permissions: data.permissions || [],
  allowedLocations: data.allowedLocations || [], // Array of districts/locations
  isActive: data.isActive !== false,
  createdBy: data.createdBy, // Email of admin who created this user
  createdAt: data.createdAt || new Date(),
  updatedAt: data.updatedAt || new Date(),
  lastLoginAt: data.lastLoginAt || null,
});

// Check if user is super admin
export const isSuperAdmin = (email) => {
  return SUPER_ADMIN_EMAILS.includes(email?.toLowerCase());
};

// Check if user has specific permission
export const hasPermission = (admin, permission) => {
  if (!admin || !admin.permissions) return false;
  return admin.permissions.includes(permission);
};

// Check if admin can access specific location
export const canAccessLocation = (admin, location) => {
  if (!admin) return false;
  
  // Super admins can access all locations
  if (admin.role === 'SUPER_ADMIN') return true;
  
  // Location-restricted admins can only access their assigned locations
  if (ADMIN_ROLES[admin.role]?.locationRestricted) {
    return admin.allowedLocations?.includes(location);
  }
  
  return false;
};

// Get admin role configuration
export const getAdminRoleConfig = (role) => {
  return ADMIN_ROLES[role] || null;
};

// Filter data based on admin permissions
export const filterDataByLocation = (data, admin, locationField = 'district') => {
  if (!admin || !data) return [];
  
  // Super admins see all data
  if (admin.role === 'SUPER_ADMIN') return data;
  
  // Location-restricted admins see only their assigned locations
  if (ADMIN_ROLES[admin.role]?.locationRestricted) {
    return data.filter(item => {
      const itemLocation = locationField.includes('.') 
        ? locationField.split('.').reduce((obj, key) => obj?.[key], item)
        : item[locationField];
      return admin.allowedLocations?.includes(itemLocation);
    });
  }
  
  return data;
};

// Validate admin creation permissions
export const canCreateAdmin = (creatorAdmin, targetRole) => {
  if (!creatorAdmin) return false;
  
  // Super Admin can create any role
  if (creatorAdmin.role === 'SUPER_ADMIN') return true;
  
  // Admin can create District Admins only
  if (creatorAdmin.role === 'ADMIN') {
    return targetRole === 'DISTRICT_ADMIN';
  }
  
  // District Admin cannot create admins
  return false;
};

// Check if admin can delete another admin
export const canDeleteAdmin = (deleterAdmin, targetAdmin) => {
  if (!deleterAdmin || !targetAdmin) return false;
  
  // Cannot delete yourself
  if (deleterAdmin.email === targetAdmin.email) return false;
  
  // Super Admin can delete anyone
  if (deleterAdmin.role === 'SUPER_ADMIN') return true;
  
  // Admin can delete District Admins only
  if (deleterAdmin.role === 'ADMIN') {
    return targetAdmin.role === 'DISTRICT_ADMIN';
  }
  
  // District Admin cannot delete anyone
  return false;
};

// Get available locations for admin assignment
export const getAvailableLocations = () => {
  return [
    'Alappuzha', 'Ernakulam', 'Idukki', 'Kannur', 'Kasaragod', 
    'Kollam', 'Kottayam', 'Kozhikode', 'Malappuram', 'Palakkad', 
    'Pathanamthitta', 'Thiruvananthapuram', 'Thrissur', 'Wayanad'
  ];
};

// Get user role from admin object or email
export const getUserRole = (adminOrEmail) => {
  if (!adminOrEmail) return null;
  
  // If it's a string (email), check if it's a super admin
  if (typeof adminOrEmail === 'string') {
    return isSuperAdmin(adminOrEmail) ? 'SUPER_ADMIN' : null;
  }
  
  // If it's an admin object, return the role
  return adminOrEmail.role || null;
};

// Get role display name
export const getRoleDisplayName = (role) => {
  return ADMIN_ROLES[role]?.name || role;
};

// Get permissions description for a role
export const getRolePermissions = (role) => {
  const config = ADMIN_ROLES[role];
  if (!config) return [];
  
  return config.permissions.map(p => {
    // Convert permission key to readable text
    return p.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  });
};
